import React, { forwardRef } from 'react';
import { COLORS, TYPOGRAPHY, INPUT_TOKENS } from '../../../styles/UISystem';

/**
 * PillInput — rounded pill-shaped input used across overlays and forms.
 *
 * Variants:
 *   bordered — white bg, MEDIUM_DARK_GRAY border (name/contact overlays)
 *   shadow   — white bg, soft shadow, no border (quick add, signup, demo)
 *   secondary — #F5F5F5 bg, no border, no shadow (location, groups)
 *
 * Right-side elements (send button, edit button) are passed as children
 * and render inside the pill after the input.
 */

const VARIANT_STYLES = {
  bordered: {
    container: {
      minHeight: '50px',
      borderRadius: '25px',
      border: INPUT_TOKENS.PILL_BORDER,
      backgroundColor: INPUT_TOKENS.PILL_BG,
    },
    containerWithChildren: {
      padding: '5px 5px 5px 16px',
    },
    containerWithoutChildren: {
      padding: '5px 5px 5px 16px',
    },
  },
  shadow: {
    container: {
      height: '48px',
      borderRadius: INPUT_TOKENS.PILL_RADIUS,
      border: 'none',
      backgroundColor: INPUT_TOKENS.PILL_BG,
      boxShadow: INPUT_TOKENS.PILL_SHADOW,
      overflow: 'hidden',
    },
    containerWithChildren: {
      padding: '0 5px 0 16px',
    },
    containerWithoutChildren: {
      padding: '0 16px',
    },
  },
  secondary: {
    container: {
      borderRadius: INPUT_TOKENS.PILL_RADIUS,
      border: 'none',
      backgroundColor: INPUT_TOKENS.PILL_BG_SECONDARY,
    },
    containerWithChildren: {
      padding: '14px 5px 14px 16px',
    },
    containerWithoutChildren: {
      padding: '14px 16px',
    },
  },
};

const PillInput = forwardRef(
  (
    {
      value,
      onChange,
      placeholder,
      variant = 'bordered',
      borderWeight,
      fontSize,
      autoComplete,
      autoCapitalize,
      maxLength,
      disabled,
      readOnly,
      type = 'text',
      inputMode,
      style,
      inputStyle,
      prefix,
      inputSlot,
      // Grows with its content and wraps: renders a <textarea> that resizes to
      // fit. Only the connection Note uses it — every other consumer stays a
      // single-line <input>.
      multiline = false,
      children,
      onFocus,
      onBlur,
      onKeyDown,
      ...rest
    },
    ref
  ) => {
    const hasChildren = !!children;
    const textareaRef = React.useRef(null);

    // Resize to content on every value change. Reset to 'auto' first, otherwise
    // scrollHeight only ever reports the current (larger) height and the field
    // can grow but never shrink back.
    React.useLayoutEffect(() => {
      if (!multiline) return;
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    }, [multiline, value]);
    const variantConfig = VARIANT_STYLES[variant] || VARIANT_STYLES.bordered;

    const containerStyle = {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      boxSizing: 'border-box',
      ...variantConfig.container,
      ...(hasChildren
        ? variantConfig.containerWithChildren
        : variantConfig.containerWithoutChildren),
      ...(borderWeight === 'heavy' && { border: `2px solid ${COLORS.MEDIUM_DARK_GRAY}` }),
      // A growing field can't keep the variant's fixed height, and its content
      // sits to the top rather than centred once it wraps to a second line.
      ...(multiline
        ? { height: 'auto', minHeight: '48px', alignItems: 'flex-start', padding: '12px 16px' }
        : {}),
      ...style,
    };

    const defaultFontSize = variant === 'secondary' ? '16px' : undefined;
    const resolvedFontSize = fontSize || defaultFontSize;

    const baseInputStyle = {
      flex: 1,
      minWidth: 0,
      border: 'none',
      outline: 'none',
      backgroundColor: 'transparent',
      lineHeight: '1.4',
      ...(resolvedFontSize ? { fontSize: resolvedFontSize } : TYPOGRAPHY.BODY_REGULAR),
      color: COLORS.DARK_GRAY,
      WebkitAppearance: 'none',
      ...(variant === 'shadow' ? { height: '100%', padding: 0 } : { padding: '10px 0' }),
      ...(variant === 'secondary' ? { padding: 0 } : {}),
      ...(multiline
        ? { height: 'auto', padding: 0, resize: 'none', overflow: 'hidden', display: 'block' }
        : {}),
      ...inputStyle,
    };

    return (
      <div style={containerStyle}>
        {prefix}
        {/* inputSlot lets a non-<input> field (e.g. PhoneInputField) use the same
            pill container, so every field shares one shape / border / height. */}
        {inputSlot ||
          (multiline ? (
            <textarea
              ref={(node) => {
                textareaRef.current = node;
                if (typeof ref === 'function') ref(node);
                else if (ref) ref.current = node;
              }}
              rows={1}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              autoCapitalize={autoCapitalize}
              maxLength={maxLength}
              disabled={disabled}
              readOnly={readOnly}
              onFocus={onFocus}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              style={baseInputStyle}
              {...rest}
            />
          ) : (
            <input
              ref={ref}
              type={type}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              autoComplete={autoComplete}
              autoCapitalize={autoCapitalize}
              maxLength={maxLength}
              inputMode={inputMode}
              disabled={disabled}
              readOnly={readOnly}
              onFocus={onFocus}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              style={baseInputStyle}
              {...rest}
            />
          ))}
        {children}
      </div>
    );
  }
);

PillInput.displayName = 'PillInput';

/** Shared @ prefix element for social handle inputs */
const AtPrefix = (
  <span
    style={{
      ...TYPOGRAPHY.BODY_REGULAR,
      color: COLORS.MEDIUM_GRAY,
      marginRight: '2px',
      flexShrink: 0,
    }}
  >
    @
  </span>
);

export { PillInput, AtPrefix };
export default PillInput;
